import type { ReactNode } from 'react';
import { motion } from 'framer-motion';

export interface RadioCardOption<T extends string> {
  id: T;
  label: string;
  description?: string;
  icon?: ReactNode;
}

interface RadioCardGroupProps<T extends string> {
  options: RadioCardOption<T>[];
  value: T;
  onChange: (value: T) => void;
  name?: string;
  className?: string;
}

export function RadioCardGroup<T extends string>({
  options,
  value,
  onChange,
  name,
  className = '',
}: RadioCardGroupProps<T>) {
  return (
    <div className={`space-y-3 ${className}`} role="radiogroup" aria-label={name}>
      {options.map((option) => {
        const isSelected = value === option.id;

        return (
          <motion.button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            whileTap={{ scale: 0.98 }}
            onClick={() => onChange(option.id)}
            className={`
              w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-colors text-left
              ${isSelected
                ? 'border-primary bg-primary/5'
                : 'border-border bg-background-card hover:border-primary/50'
              }
            `}
          >
            {option.icon && (
              <div
                className={`
                  w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                  ${isSelected
                    ? 'bg-primary text-text-inverse'
                    : 'bg-background text-text-secondary'
                  }
                `}
              >
                {option.icon}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p
                className={`
                  font-medium
                  ${isSelected ? 'text-primary' : 'text-text-primary'}
                `}
              >
                {option.label}
              </p>
              {option.description && (
                <p className="text-sm text-text-secondary">{option.description}</p>
              )}
            </div>
            {isSelected && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0"
              >
                <div className="w-2 h-2 rounded-full bg-text-inverse" />
              </motion.div>
            )}
          </motion.button>
        );
      })}
    </div>
  );
}
